import { useEffect, useMemo, useRef, useState } from "react";
import { ago, fullDay, plural, sameDay } from "@shared/lib/format";
import { fromSelf, livePerson } from "@entities/user";
import type { CustomEmoji } from "@shared/lib/emoji";
import { useMe } from "@entities/session";
import { ThreadHeadMenu, ThreadMenuOn, useStore, useThreadActions } from "@features/chat";
import { MessageRow } from "./MessageRow";
import { Composer } from "./Composer";
import { Tile } from "@entities/user";
import type { Channel, Message, Named, Role, ServerSticker, ThreadListItem, Uuid } from "@shared/api";
import {
  IconAnnouncementChannel,
  IconBell,
  IconChevronRight,
  IconClose,
  IconForumChannel,
  IconMembers,
  IconMore,
  IconPinned,
  IconSilent,
  IconTextChannel,
  IconThreads,
} from "@shared/ui/icons";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@shared/ui/popover";
import { plainText } from "@shared/lib/markup";
import { Markup } from "@shared/ui/Markup";
import { Input } from "@shared/ui/input";
import { SLOWMODES } from "@shared/config";
import { Hint } from "@shared/ui/bits";
import { useSearch } from "../model/search";
import { PinnedList } from "./PinnedList";

/**
 * Третья колонка: ветка или список веток канала.
 *
 * Колонка одна и встаёт на место участников — не поверх ленты и не отдельным
 * окном: ветка часть разговора, а не выход из него. Лента рядом остаётся живой
 * и полной, только уже.
 */
export function ThreadPanel({
  people,
  custom,
  channels,
  roles,
  myRoles,
  onOpenProfile,
  onOpenChannel,
  canSend,
  canReact,
  canDeleteOthers,
  canPin,
  canManageThreads,
  full = false,
  search,
  membersOpen = false,
  onToggleMembers,
  ...writing
}: {
  people: Map<Uuid, Named>;
  /** Знаки сервера: ветка живёт на нём же, и знаки в ней те же. */
  custom: CustomEmoji[];
  /** Каналы сервера: упоминания в ветке те же, что и в ленте. */
  channels: Channel[];
  /** Роли сервера: и они тоже — ветка не отдельный мир. */
  roles: Role[];
  myRoles: Uuid[];
  onOpenProfile: (userId: Uuid, anchor?: HTMLElement) => void;
  onOpenChannel: (channelId: Uuid) => void;
  /** Писать в ветке — своё право, как в Discord. */
  canSend: boolean;
  canReact: boolean;
  canDeleteOthers: boolean;
  /** Закреплять — своё право, как в Discord. */
  canPin: boolean;
  /** Отвечать в запертой ветке — правом управлять ветками. */
  canManageThreads: boolean;
  /**
   * Ветка на месте ленты — «Открыть на весь экран», как в Discord: шапка с
   * путём «# канал › ветка», ответы во всю ширину, поле внизу.
   */
  full?: boolean;
  /** Поле поиска шапки — у ветки на весь экран шапки канала нет, и поле стоит в её шапке. */
  search?: React.ReactNode;
  /** Кто в ветке справа — значком в шапке, как участники у канала. */
  membersOpen?: boolean;
  onToggleMembers?: () => void;
} & Writing) {
  const store = useStore();
  const panel = store.panel;

  if (!panel) return null;

  if (full) {
    if (panel.kind !== "thread") return null;
    return (
      <section className="chat chat-feed thread-full">
        <Branch
          full
          search={search}
          membersOpen={membersOpen}
          onToggleMembers={onToggleMembers}
          channelId={panel.channelId}
          threadId={panel.threadId ?? null}
          rootId={panel.rootId!}
          people={people}
          custom={custom}
          channels={channels}
          roles={roles}
          myRoles={myRoles}
          onOpenProfile={onOpenProfile}
          onOpenChannel={onOpenChannel}
          canSend={canSend}
          canReact={canReact}
          canDeleteOthers={canDeleteOthers}
          canPin={canPin}
          canManageThreads={canManageThreads}
          {...writing}
        />
      </section>
    );
  }

  return (
    // Файл, брошенный над веткой, — в её поле, а не в поле ленты рядом (`Composer`).
    <aside className="panel panel-thread glass-host" data-drop-zone={panel.kind === "thread" ? "thread" : undefined}>
      {panel.kind === "list" ? (
        <ThreadList channelId={panel.channelId} people={people} />
      ) : (
        <Branch
          channelId={panel.channelId}
          threadId={panel.threadId ?? null}
          rootId={panel.rootId!}
          people={people}
          custom={custom}
          channels={channels}
          roles={roles}
          myRoles={myRoles}
          onOpenProfile={onOpenProfile}
          onOpenChannel={onOpenChannel}
          canSend={canSend}
          canReact={canReact}
          canDeleteOthers={canDeleteOthers}
          canPin={canPin}
          canManageThreads={canManageThreads}
          {...writing}
        />
      )}
    </aside>
  );
}

/** Что нужно полю ветки сверх показа — то же, что полю канала. */
interface Writing {
  /** Сервер ветки: «/nick» и «/timeout» в её поле. `null` — ветка в переписке, заведённая раньше. */
  serverId: Uuid | null;
  canUpload: boolean;
  /** Голосовые — своим правом, как в Discord. */
  canVoiceMessage: boolean;
  canEveryone: boolean;
  /** Медленный режим ветки меня не держит — по тем же правам, что в канале. */
  slowExempt: boolean;
  stickers: ServerSticker[];
  onModerate: (kind: "kick" | "ban", userId: Uuid) => void;
  onDirectMessage: (userId: Uuid, content: string) => Promise<void>;
}

function Branch({
  full = false,
  search,
  membersOpen = false,
  onToggleMembers,
  channelId,
  threadId,
  rootId,
  people,
  custom,
  channels,
  roles,
  myRoles,
  onOpenProfile,
  onOpenChannel,
  canSend,
  canReact,
  canDeleteOthers,
  canPin,
  canManageThreads,
  serverId,
  canUpload,
  canVoiceMessage,
  canEveryone,
  slowExempt,
  stickers,
  onModerate,
  onDirectMessage,
}: {
  full?: boolean;
  search?: React.ReactNode;
  membersOpen?: boolean;
  onToggleMembers?: () => void;
  channelId: Uuid;
  threadId: Uuid | null;
  rootId: Uuid;
  people: Map<Uuid, Named>;
  custom: CustomEmoji[];
  channels: Channel[];
  roles: Role[];
  myRoles: Uuid[];
  onOpenProfile: (userId: Uuid, anchor?: HTMLElement) => void;
  onOpenChannel: (channelId: Uuid) => void;
  canSend: boolean;
  canReact: boolean;
  canDeleteOthers: boolean;
  canPin: boolean;
  canManageThreads: boolean;
} & Writing) {
  const me = useMe();
  const store = useStore();
  const thread = threadId ? store.thread(threadId) : undefined;
  const replies = threadId ? store.threadMessages(threadId) : undefined;

  /*
   * Корень берётся из ленты канала: он там уже есть, и второй запрос ради
   * того же сообщения был бы запросом ни за чем. Ветку открыли из списка, а
   * корень вне окна — тогда его приносит сама ветка.
   */
  const fromFeed = store.messages(channelId)?.find((item) => item.id === rootId);
  const root = fromFeed ?? thread?.root ?? null;

  const nameOf = (handle: string) => {
    for (const person of people.values()) {
      if (person.handle === handle) return person.display_name || person.username;
    }
    return null;
  };

  /** Имя канала по номеру — упоминания в ветке те же, что и в ленте. */
  const channelOf = useMemo(() => {
    const byId = new Map(channels.map((item) => [item.id, item.name]));
    return (id: string) => byId.get(id) ?? null;
  }, [channels]);

  const roleOf = useMemo(() => {
    const byId = new Map(roles.map((item) => [item.id, item.name]));
    return (id: string) => byId.get(id) ?? null;
  }, [roles]);

  /** Знак сервера по имени — для корня, как в ленте. */
  const emojiOf = useMemo(() => {
    const byName = new Map(custom.map((item) => [item.name, item]));
    return (name: string) => byName.get(name) ?? null;
  }, [custom]);

  /*
   * Граница «новые» замирает на том, чем она была в момент открытия.
   *
   * Иначе её не увидеть вовсе: панель тут же отмечает ветку прочитанной,
   * отметка уезжает на последний ответ, и черта исчезает в том же кадре, в
   * котором появилась. Ровно так же заморожена черта в ленте канала.
   */
  const boundary = useRef<{ threadId: Uuid; any: boolean; at: string | null } | null>(null);

  if (threadId && boundary.current?.threadId !== threadId && thread) {
    /*
     * `at` пустой значит две разные вещи, поэтому рядом с ним `any`: ветку
     * не открывали ни разу — черта идёт над первым ответом; непрочитанного
     * не было — черты нет вовсе.
     */
    boundary.current = {
      threadId,
      any: thread.unread > 0,
      at: thread.last_read_id,
    };
  }

  /*
   * Прочитанным ветка становится, когда её видели: панель открыта и в ней
   * есть последний ответ. Правило то же, что в ленте, только считать нечего —
   * ветка короткая и помещается целиком.
   */
  /*
   * Отметка едет один раз на пару «ветка + последний ответ».
   *
   * Прежде она уходила на каждое изменение длины списка, а список меняется по
   * своим причинам: пришёл ответ, догрузилась страница, вернулось событие с
   * узла. Пока ответ на первую отметку в пути, `unread` ещё не ноль — и вторая
   * успевала уйти следом. Здесь запоминается, что уже отмечено, и повтор
   * молчит.
   */
  const marked = useRef<string | null>(null);

  useEffect(() => {
    if (!threadId || !replies?.length) return;
    if (!thread || thread.unread === 0) return;

    const last = replies[replies.length - 1];
    const mark = `${threadId}|${last.id}`;
    if (marked.current === mark) return;

    marked.current = mark;
    void store.markThreadRead(channelId, threadId, last.id);
    /*
     * Зависимости названы поимённо: список ответов и склейка сторов меняют
     * тождество на каждое событие, а отметка едет от появления нового ответа и
     * от того, осталось ли непрочитанное.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, replies?.length, thread?.unread]);

  const empty = !threadId;

  /**
   * «Начато: …» — хозяин ветки, кто её завёл, как в Discord, а не автор
   * сообщения, на котором она выросла. Он в ветке говорил, и его лицо есть
   * среди участников; нет там — ищем среди людей сервера.
   */
  const ownerId = thread?.owner_id ?? null;
  const owner = ownerId ? (thread?.participants.find((item) => item.id === ownerId) ?? people.get(ownerId)) : undefined;
  const starter = owner ? owner.display_name || owner.username : null;
  /** Колокольчик перечёркнут, как в Discord, — не в ветке или заглушена. */
  const silent = !!thread && (!thread.followed || thread.notify_level === "none");
  /** Имя ветки — своё, если назвали; иначе начало корня, как в списках веток и под каналом. */
  /*
   * Имя ветки — своё. У ветки без имени (заведённой раньше, чем имя стало
   * ставиться при создании) — начало корня без разметки, и метка в нём —
   * датой: имя не тикает. «Живой» вид метки («через 3 минуты») — только на
   * шаге «Новая ветка»: там это подсказка, которая станет именем текстом.
   */
  const firstLine = root?.content.split("\n")[0] ?? "";
  const name =
    thread?.title ||
    plainText(firstLine, { channelOf, roleOf, live: empty }).slice(0, 80) ||
    "Ветка";
  const parent = channels.find((item) => item.id === channelId);
  const channelName = parent?.name ?? "канал";
  const ChannelIcon =
    parent?.channel_type === "announcement"
      ? IconAnnouncementChannel
      : parent?.channel_type === "forum"
        ? IconForumChannel
        : IconTextChannel;
  const [menu, setMenu] = useState<{ kind: "bell" | "more"; x: number; y: number } | null>(null);
  const [pins, setPins] = useState(false);
  /** «Название ветки» на шаге «Новая ветка». */
  const [newName, setNewName] = useState("");
  const [list, setList] = useState(false);
  const feed = useRef<HTMLDivElement>(null);
  const { items, bell, dialogs } = useThreadActions(
    {
      channelId,
      threadId: threadId ?? "",
      rootId,
      ownerId: thread?.owner_id,
      followed: thread?.followed ?? false,
      notifyLevel: thread?.notify_level ?? null,
      notifyUntil: thread?.notify_until,
      name: thread?.title,
      closed: thread?.closed,
      locked: thread?.locked,
      unread: thread?.unread,
    },
    {
      kind: "head",
      full,
      /*
       * «Поиск» — как в Discord: поле поиска в шапке с плашкой «в ветке»;
       * искать будет по каналу ветки, только её ответы.
       */
      onSearch: () => {
        if (!threadId) return;
        const search = useSearch.getState();
        search.patch({ thread: { id: threadId, name } });
        search.setScope("channel");
        window.setTimeout(() => document.querySelector<HTMLInputElement>(".head-search-input")?.focus());
      },
      onPins: () => window.setTimeout(() => setPins(true)),
      onTop: () => feed.current?.scrollTo({ top: 0, behavior: "smooth" }),
    },
  );

  // Ушли из ветки — плашка «в ветке» в поиске больше ни к чему не относится.
  useEffect(
    () => () => {
      const search = useSearch.getState();
      if (threadId && search.thread?.id === threadId) search.patch({ thread: null });
    },
    [threadId],
  );

  /** Меню у кнопки — от её нижнего края, как у меню звука и микрофона. */
  const openFrom = (kind: "bell" | "more") => (event: React.MouseEvent<HTMLElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    setMenu({ kind, x: box.right - 248, y: box.bottom + 6 });
  };

  /** Присоединиться, колокольчик и «…» — одни и те же в колонке и на весь экран. */
  const tools = (
    <>
      {threadId && (
        <>
          <Hint label="Параметры уведомлений" side="bottom">
            <button
              type="button"
              className={`icon-btn ${silent ? "muted" : ""}`}
              aria-label="Параметры уведомлений"
              onClick={openFrom("bell")}
            >
              {silent ? <IconSilent size={15} /> : <IconBell size={15} />}
            </button>
          </Hint>
          <Hint label="Ещё" side="bottom">
            <button type="button" className="icon-btn" aria-label="Ещё" onClick={openFrom("more")}>
              <IconMore size={15} />
            </button>
          </Hint>
        </>
      )}
    </>
  );

  const close = (
    <Hint label={full ? "Вернуться в канал" : "Закрыть"} side="bottom">
      <button type="button" className="icon-btn" aria-label="Закрыть" onClick={store.closePanel}>
        <IconClose size={14} />
      </button>
    </Hint>
  );

  /*
   * На весь экран шапка — как у текстового канала: две плашки над лентой,
   * слева путь «# канал › ветка», справа значки и поиск. Общей подложки нет:
   * лента идёт во всё окно, как в канале.
   */
  const header = full ? (
    <header className="main-head chat-head thread-full-head">
      {/*
        Путь — теми же значками и шрифтом, что шапка канала: значок канала,
        его имя (щелчок возвращает к нему), стрелка, значок ветки и её имя.
      */}
      <div className="chat-head-title glass-host">
        <ChannelIcon size={18} className="head-title-icon" />
        <button type="button" className="head-title-name thread-crumb ellipsis" onClick={store.closePanel}>
          {channelName}
        </button>
        <IconChevronRight size={16} className="thread-crumb-sep" />
        <IconThreads size={18} className="head-title-icon" />
        <span className="head-title-name ellipsis" title={thread?.title ?? undefined}>
          {name}
        </span>
      </div>
      {/*
        Справа — как в шапке ветки Discord на весь экран: ветки канала,
        уведомления, закреплённые, кто в ветке, «…» и поиск. Крестика нет:
        к каналу ведёт путь слева.
      */}
      <div className="chat-head-side glass-host">
        <Popover open={list} onOpenChange={setList}>
          <Hint label="Ветки" side="bottom">
            <PopoverTrigger asChild>
              <button type="button" className={`chat-head-tool ${list ? "on" : ""}`} aria-label="Ветки">
                <IconThreads size={18} />
              </button>
            </PopoverTrigger>
          </Hint>
          <PopoverContent align="end" sideOffset={12} onOpenAutoFocus={(event) => event.preventDefault()}>
            <div
              className="head-pop glass-host"
              onClickCapture={(event) => {
                if ((event.target as HTMLElement).closest(".thread-row")) window.setTimeout(() => setList(false));
              }}
            >
              <ThreadList standalone channelId={channelId} people={people} onClose={() => setList(false)} />
            </div>
          </PopoverContent>
        </Popover>
        {threadId && (
          <>
            <Hint label="Параметры уведомлений" side="bottom">
              <button
                type="button"
                className={`chat-head-tool ${silent ? "muted" : ""}`}
                aria-label="Параметры уведомлений"
                onClick={openFrom("bell")}
              >
                {silent ? <IconSilent size={18} /> : <IconBell size={18} />}
              </button>
            </Hint>
            <Hint label="Закреплённые сообщения" side="bottom">
              <button
                type="button"
                className={`chat-head-tool ${pins ? "on" : ""}`}
                aria-label="Закреплённые сообщения"
                onClick={() => setPins(!pins)}
              >
                <IconPinned size={18} />
              </button>
            </Hint>
          </>
        )}
        {onToggleMembers && (
          <Hint label={membersOpen ? "Скрыть, кто в ветке" : "Показать, кто в ветке"} side="bottom">
            <button
              type="button"
              className={`chat-head-tool ${membersOpen ? "on" : ""}`}
              aria-label="Кто в ветке"
              onClick={onToggleMembers}
            >
              <IconMembers size={18} />
            </button>
          </Hint>
        )}
        {threadId && (
          <Hint label="Ещё" side="bottom">
            <button type="button" className="chat-head-tool" aria-label="Ещё" onClick={openFrom("more")}>
              <IconMore size={18} />
            </button>
          </Hint>
        )}
        {search && <span className="thread-full-search">{search}</span>}
      </div>
    </header>
  ) : (
    <header className="side-head">
      <span className="side-head-icon">
        <IconThreads size={14} />
      </span>
      <span className="side-head-name ellipsis" title={thread?.title ?? undefined}>
        {empty ? "Новая ветка" : name}
      </span>
      <span className="grow" />
      {tools}
      {close}
    </header>
  );

  /** Закрытая ветка, корень и ответы — в колонке как были, на весь экран — внутри ленты. */
  const content = (
    <>
      {/* Закрытая ветка видна, но ушла из списков: ответ вернёт её, как в Discord. */}
      {thread?.closed && (
        <div className="thread-closed-note">Ветка закрыта. Ответ в ней откроет её снова.</div>
      )}

      <div className="thread-root">
        {root ? (
          <>
            <Tile person={livePerson(root.author, people, fromSelf(me))} size={30} mark={null} />
            <div style={{ minWidth: 0 }}>
              <div className="thread-root-head">
                <span className="thread-root-name">
                  {root.author.display_name || root.author.username}
                </span>
                <span className="thread-root-at">{ago(root.created)}</span>
              </div>
              {/* Корень — с разметкой, как в ленте: метки времени, упоминания, знаки. */}
              <div className="thread-root-text">
                <Markup
                  text={root.content}
                  nameOf={nameOf}
                  mine={me.handle}
                  emojiOf={emojiOf}
                  channelOf={channelOf}
                  roleOf={roleOf}
                  onOpenChannel={onOpenChannel}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="thread-root-gone">Сообщение удалено — ветка осталась</div>
        )}
      </div>

      {empty ? null : (
        <div className="thread-replies">
          <div className="thread-count-rule">
            <span>
              {thread?.reply_count ?? 0}{" "}
              {plural(thread?.reply_count ?? 0, "ответ", "ответа", "ответов")}
            </span>
            <span className="rule" />
          </div>

          {replies?.map((reply, index) => (
            <ReplyRow
              key={reply.id}
              reply={reply}
              previous={replies[index - 1]}
              firstNew={
                (boundary.current?.any ?? false) &&
                (boundary.current?.at
                  ? replies[index - 1]?.id === boundary.current.at
                  : index === 0)
              }
              channelId={channelId}
              nameOf={nameOf}
              mine={me.handle}
              own={reply.author.id === me.id}
              canReact={canReact}
              canDeleteOthers={canDeleteOthers}
              canPin={canPin}
              custom={custom}
              people={people}
              channelOf={channelOf}
              roleOf={roleOf}
              myRoles={myRoles}
              onOpenProfile={onOpenProfile}
              onOpenChannel={onOpenChannel}
            />
          ))}
        </div>
      )}
    </>
  );

  return (
    <>
      <Popover open={pins} onOpenChange={setPins}>
        <PopoverAnchor asChild>{header}</PopoverAnchor>
        <PopoverContent align="end" sideOffset={8} onOpenAutoFocus={(event) => event.preventDefault()}>
          <div className="head-pop glass-host">
            <div className="side-head">
              <span className="side-head-name">Закреплённые в ветке</span>
              <span className="grow" />
              <button type="button" className="icon-btn" aria-label="Закрыть" onClick={() => setPins(false)}>
                <IconClose size={14} />
              </button>
            </div>
            {threadId && (
              <PinnedList
                channelId={channelId}
                threadId={threadId}
                canPin={canPin}
                people={people}
                custom={custom}
                channels={channels}
                roles={roles}
                onOpenChannel={onOpenChannel}
                onOpenProfile={onOpenProfile}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      {menu && (
        <ThreadHeadMenu
          // У колокольчика заголовка нет, как в Discord: меню начинается с «Заглушить ветку».
          title={menu.kind === "bell" ? undefined : "ВЕТКА"}
          at={{ x: Math.max(8, menu.x), y: menu.y }}
          items={menu.kind === "bell" ? bell : items}
          onClose={() => setMenu(null)}
        />
      )}
      {dialogs}

      {/*
        Лента ветки — заголовок (значок, имя, кто начал), корень и ответы
        прокручиваются вместе, как в Discord. На весь экран — как лента
        канала: во всё окно, края растворяются.
      */}
      <div ref={feed} className={full ? "feed thread-full-feed" : `thread-scroll ${empty ? "new" : ""}`}>
        {empty ? (
          /*
           * «Новая ветка» — как в Discord: имя задают сразу, оно необязательно;
           * подсказка в поле — то, как ветку назовут без него.
           */
          <div className="thread-intro">
            <span className="thread-intro-icon">
              <IconThreads size={26} />
            </span>
            <label className="thread-new-name">
              <span className="thread-new-label">Название ветки (необязательно)</span>
              <Input
                value={newName}
                maxLength={100}
                placeholder={name}
                onChange={(event) => setNewName(event.target.value)}
              />
            </label>
          </div>
        ) : (
          <div className="thread-intro">
            <span className="thread-intro-icon">
              <IconThreads size={26} />
            </span>
            <h2 className="thread-intro-name">{name}</h2>
            {starter && (
              <div className="thread-intro-by">
                Начато: <b>{starter}</b>
              </div>
            )}
          </div>
        )}
        {content}
      </div>

      {/*
        Поле — то же, что у ленты канала: вложения, голосовое, время, стикеры,
        плашки. Своё поле ветки умело только текст и знаки.
      */}
      {canSend && (!thread?.locked || canManageThreads) && (
        <div className="thread-composer">
          <Composer
            key={rootId}
            channelId={channelId}
            thread={{
              rootId,
              threadId,
              /*
               * Как в Discord: не назвали — ветка получает имя из подсказки, текстом
               * на момент создания («через 3 минуты» так и останется), а не меткой,
               * которая пересчитывалась бы.
               */
              title: empty ? newName.trim() || name : null,
            }}
            placeholder={empty ? "Введите сообщение, чтобы начать беседу!" : "Ответить в ветке…"}
            people={people}
            custom={custom}
            channels={channels}
            roles={roles}
            canUpload={canUpload}
            canVoiceMessage={canVoiceMessage}
            canEveryone={canEveryone}
            stickers={stickers}
            serverId={serverId}
            slowmode={slowExempt ? 0 : (thread?.slowmode ?? 0)}
            onModerate={onModerate}
            onDirectMessage={onDirectMessage}
          />
          {/* Медленный режим у ветки свой; держит сервер, здесь — чтобы о нём знали заранее. */}
          {(thread?.slowmode ?? 0) > 0 && (
            <div className="thread-composer-note">
              Медленный режим: одно сообщение раз в{" "}
              {(
                SLOWMODES.find((item) => item.seconds === thread!.slowmode)?.label ?? `${thread!.slowmode} секунд`
              ).toLowerCase()}
            </div>
          )}
        </div>
      )}
      {canSend && thread?.locked && !canManageThreads && (
        <div className="rules-bar">Ветка заблокирована — отвечать в ней больше нельзя.</div>
      )}
    </>
  );
}

function ReplyRow({
  reply,
  previous,
  firstNew,
  channelId,
  nameOf,
  mine,
  own,
  canReact,
  canDeleteOthers,
  canPin,
  custom,
  people,
  channelOf,
  roleOf,
  myRoles,
  onOpenProfile,
  onOpenChannel,
}: {
  reply: Message;
  previous: Message | undefined;
  firstNew: boolean;
  channelId: Uuid;
  nameOf: (handle: string) => string | null;
  mine: string;
  own: boolean;
  canReact: boolean;
  canDeleteOthers: boolean;
  canPin: boolean;
  custom: CustomEmoji[];
  people: Map<Uuid, Named>;
  channelOf: (id: string) => string | null;
  roleOf: (id: string) => string | null;
  myRoles: Uuid[];
  onOpenProfile: (userId: Uuid, anchor?: HTMLElement) => void;
  onOpenChannel: (channelId: Uuid) => void;
}) {
  const store = useStore();
  const head = !previous || previous.author.id !== reply.author.id || !sameDay(previous.created, reply.created);

  /** Знак сервера по имени — тот же поиск, что в ленте. */
  const emojiOf = useMemo(() => {
    const byName = new Map(custom.map((item) => [item.name, item]));
    return (name: string) => byName.get(name) ?? null;
  }, [custom]);

  return (
    <>
      {firstNew && (
        <div className="feed-new">
          <span className="feed-new-rule" />
          новые
        </div>
      )}
      {(!previous || !sameDay(previous.created, reply.created)) && (
        <div className="feed-edge feed-divider">
          <span className="rule" />
          {fullDay(reply.created)}
          <span className="rule" />
        </div>
      )}
      <MessageRow
        message={reply}
        people={people}
        head={head}
        own={own}
        canDeleteOthers={canDeleteOthers}
        canReact={canReact}
        // Закрепить ответ — как в канале, тем, кто управляет сообщениями: у ветки свои закреплённые.
        canPin={canPin}
        onPin={() => void store.setPinned(channelId, reply.id, !reply.pinned)}
        // Ветка от ответа не заводится: уровень один, иначе это дерево.
        canSend={false}
        nameOf={nameOf}
        mine={mine}
        roleOf={roleOf}
        myRoles={myRoles}
        onSeen={() => {}}
        onEdit={(content, remove) => store.edit(channelId, reply.id, content, remove)}
        onDelete={() => void store.remove(channelId, reply.id)}
        onOpenProfile={onOpenProfile}
        onReact={(emoji, isMine) => void store.toggleReaction(channelId, reply.id, emoji, isMine)}
        custom={custom}
        emojiOf={emojiOf}
        channelOf={channelOf}
        onOpenChannel={onOpenChannel}
        onReply={() => {}}
        onJumpTo={() => {}}
        onOpenThread={() => {}}
        threadOpen={false}
        channelId={channelId}
      />
    </>
  );
}

/**
 * Список веток канала. В колонке справа его грузит открытие колонки; в окне
 * под значком шапки (`standalone`) — он сам, при показе и смене «Слежу / Все».
 */
export function ThreadList({
  channelId,
  people,
  onClose,
  standalone = false,
}: {
  channelId: Uuid;
  people: Map<Uuid, Named>;
  onClose?: () => void;
  standalone?: boolean;
}) {
  const store = useStore();
  const items = store.threadList(channelId);
  const { loadThreads, threadFilter } = store;
  useEffect(() => {
    if (standalone) loadThreads(channelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standalone, channelId, threadFilter]);

  // Закрытая ветка — к затихшим, как архив веток в Discord: ответ вернёт её в активные.
  const active = items?.filter((item) => !item.quiet && !item.closed) ?? [];
  const quiet = items?.filter((item) => item.quiet || item.closed) ?? [];

  return (
    <>
      <header className="side-head">
        <span className="side-head-icon"><IconThreads size={14} /></span>
        <span className="side-head-name">Ветки</span>
        <span className="grow" />
        <button type="button" className="icon-btn" title="Закрыть" onClick={onClose ?? store.closePanel}>
          <IconClose size={14} />
        </button>
      </header>

      <div className="thread-filter">
        {[
          { label: "Слежу", value: true },
          { label: "Все", value: false },
        ].map((tab) => (
          <button
            key={tab.label}
            type="button"
            className={store.threadFilter === tab.value ? "on" : ""}
            onClick={() => store.setThreadFilter(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="thread-list">
        {items === undefined && <div className="thread-list-note">читаю ветки…</div>}

        {items?.length === 0 && (
          <div className="thread-list-note">
            {store.threadFilter
              ? "Вы пока ни за одной веткой не следите."
              : "В этом канале веток ещё нет."}
          </div>
        )}

        {active.length > 0 && <div className="thread-list-title">АКТИВНЫЕ</div>}
        {active.map((item) => (
          <ThreadListRow
            key={item.id}
            item={item}
            channelId={channelId}
            people={people}
          />
        ))}

        {quiet.length > 0 && <div className="thread-list-title">ЗАТИХШИЕ</div>}
        {quiet.map((item) => (
          <ThreadListRow
            key={item.id}
            item={item}
            channelId={channelId}
            people={people}
          />
        ))}
      </div>
    </>
  );
}

function ThreadListRow({
  item,
  channelId,
  people,
}: {
  item: ThreadListItem;
  channelId: Uuid;
  people: Map<Uuid, Named>;
}) {
  const store = useStore();
  const me = fromSelf(useMe());

  return (
    <ThreadMenuOn
      thread={{
        channelId,
        threadId: item.id,
        rootId: item.root_message_id,
        ownerId: item.owner_id,
        followed: item.followed,
        notifyLevel: item.notify_level,
        notifyUntil: item.notify_until,
        name: item.title,
        closed: item.closed,
        locked: item.locked,
        unread: item.unread,
      }}
    >
      <button
        type="button"
        className={`thread-row ${item.quiet ? "quiet" : ""} ${item.unread > 0 ? "fresh" : ""}`}
        onClick={() => item.root_message_id && store.openThread(channelId, item.root_message_id, item.id)}
      >
        {/* Скобка слева — признак слежения, акцентная при непрочитанном. */}
        {item.followed && <span className={`thread-row-mark ${item.unread > 0 ? "on" : ""}`} />}
        <span className="thread-row-title">
          {item.title || (item.excerpt ? plainText(item.excerpt) : "Сообщение удалено")}
        </span>
        <span className="thread-row-meta">
          <span className="thread-faces">
            {item.participants.slice(0, 4).map((person) => (
              <Tile key={person.id} person={livePerson(person, people, me)} size={15} mark={null} />
            ))}
          </span>
          <span>
            {item.reply_count} {plural(item.reply_count, "ответ", "ответа", "ответов")}
            {item.last_reply_at ? ` · ${ago(item.last_reply_at)}` : ""}
          </span>
        </span>
        {item.mentions > 0 ? (
          <span className="badge">{item.mentions}</span>
        ) : item.unread > 0 ? (
          <span className="thread-row-dot" />
        ) : null}
      </button>
    </ThreadMenuOn>
  );
}
